/**
 * Utility functions for handling multilanguage data in the frontend
 */

/**
 * Get text value for a specific language from a multilanguage field
 * @param {Object|string} field - Multilanguage field (object with language keys) or string
 * @param {string} language - Language code (default: 'en')
 * @returns {string} - Text value for the language
 */
export const getLanguageText = (field, language = 'en') => {
  if (!field) return '';
  
  // If it's already a string (old format or single language), return as is
  if (typeof field === 'string') {
    return field;
  }
  
  // If it's an object with language keys
  if (typeof field === 'object' && field !== null) {
    return field[language] || field['en'] || field[Object.keys(field)[0]] || '';
  }
  
  return '';
};

/**
 * Set text value for a specific language in a multilanguage field
 * @param {Object} field - Multilanguage field (object with language keys)
 * @param {string} language - Language code
 * @param {string} value - Text value to set
 * @returns {Object} - Updated multilanguage object
 */
export const setLanguageText = (field, language, value) => {
  if (!field || typeof field !== 'object') {
    field = {};
  }
  
  return {
    ...field,
    [language]: value
  };
};

/**
 * Get available languages from a multilanguage field
 * @param {Object} field - Multilanguage field (object with language keys)
 * @returns {Array} - Array of language codes
 */
export const getAvailableLanguages = (field) => {
  if (!field || typeof field !== 'object') {
    return ['en'];
  }
  
  return Object.keys(field);
};

/**
 * Check if a field has multilanguage support
 * @param {any} field - Field to check
 * @returns {boolean} - True if field has multilanguage support
 */
export const isMultilanguageField = (field) => {
  return field && typeof field === 'object' && field !== null && !Array.isArray(field);
};

/**
 * Convert old string fields to multilanguage format
 * @param {string} text - Text to convert
 * @param {string} language - Language code (default: 'en')
 * @returns {Object} - Multilanguage object
 */
export const convertToMultilanguage = (text, language = 'en') => {
  return {
    [language]: text || ''
  };
};

/**
 * Truncate multilanguage text for display
 * @param {Object|string} field - Multilanguage field or string
 * @param {number} maxLength - Maximum length
 * @param {string} language - Language code (default: 'en')
 * @returns {string} - Truncated text
 */
export const truncateLanguageText = (field, maxLength, language = 'en') => {
  const text = getLanguageText(field, language);
  
  if (!text) return '';
  
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};